// PeerSession — one RTCPeerConnection to one remote peer, signaled through RoomClient.
// Ported from the lab (spikes/p2p-lab/public/app/js/rtc.js), where every rule below was
// found on real phones — see spikes/p2p-lab/README.md.
//
// No glare: the peer with the smaller id always makes the offer. Every connection attempt has
// a generation number (`gen`) so late candidates from a dead attempt are dropped. A negotiated
// 'ctl' channel (id 0) exists on both sides before the offer (no ondatachannel race); bulk
// transfers open their own channels on the same SCTP association.
//
// Network changes (wifi ↔ 4G): first an ICE restart on the same connection — the SCTP
// association and its channels survive, a running transfer continues on the new path. If the
// connection is not back after ESCALATE_MS, a full restart (new generation, channels lost, the
// application resumes by offset). Offers and candidates are resent until the connection is up,
// because a signal for a peer that is momentarily offline can be lost.
//
// Events: 'ready' (ctl open), 'down' (ctl closed), 'ctl' {msg}, 'channel' {channel}, 'state' {state}.

import type { RoomClient } from './signal';

const DISCONNECTED_GRACE_MS = 3000;
const ESCALATE_MS = 10_000;
const MAX_EVENTS = 60;
const OFFER_RESEND_MS = 4000;
const OFFER_RESENDS = 10;
const CANDIDATE_RESEND_MS = 3000;
const CANDIDATE_RESENDS = 10;

export type Signal =
  | { kind: 'desc'; gen: number; sdp: RTCSessionDescriptionInit }
  | { kind: 'cand'; gen: number; cand: RTCIceCandidateInit }
  | { kind: 'restart' }
  | { kind: 'ice-restart'; reason: string };

export type PathInfo = {
  local: string;
  remote: string;
  protocol: string;
  ipVersion: 4 | 6 | null;
  rttMs: number | null;
  maxMessageSize: number | null;
};

export class PeerSession extends EventTarget {
  pc: RTCPeerConnection | null = null;
  ctl: RTCDataChannel | null = null;
  gen = 0;
  lastPath: PathInfo | null = null;
  readonly events: [number, string][] = [];
  readonly timings = { pcConnectedMs: null as number | null, reconnects: 0, iceRestarts: 0 };

  readonly #t0 = performance.now();
  #pendingCandidates: RTCIceCandidateInit[] = [];
  #localCandidates: RTCIceCandidateInit[] = [];
  #escalation: ReturnType<typeof setTimeout> | undefined;
  #offerTimer: ReturnType<typeof setInterval> | undefined;
  #candTimer: ReturnType<typeof setInterval> | undefined;
  #chain: Promise<void> = Promise.resolve();

  constructor(
    readonly room: RoomClient,
    readonly self: string,
    readonly remote: string,
    readonly iceServers: RTCIceServer[],
    readonly log: (line: string) => void = () => {},
  ) {
    super();
  }

  get initiator(): boolean {
    return this.self < this.remote;
  }

  get ready(): boolean {
    return this.ctl?.readyState === 'open';
  }

  /** Diagnostic trail: [ms since session start, event]. */
  ev(name: string): void {
    this.events.push([Math.round(performance.now() - this.#t0), name]);
    if (this.events.length > MAX_EVENTS) this.events.shift();
  }

  start(): void {
    if (this.initiator) this.#open(1);
  }

  /** Drops the current connection and builds a new one (channels are lost). */
  restart(reason = 'manual'): void {
    this.timings.reconnects++;
    this.ev(`restart:${reason}`);
    clearTimeout(this.#escalation);
    if (this.initiator) this.#open(this.gen + 1);
    else {
      this.pc?.close();
      this.room.send(this.remote, { kind: 'restart' } satisfies Signal);
    }
  }

  /** Finds a new network path while keeping the connection and its data channels. */
  async iceRestart(reason: string): Promise<void> {
    this.ev(`ice-restart:${reason}`);
    if (!this.initiator) {
      this.room.send(this.remote, { kind: 'ice-restart', reason } satisfies Signal);
      return;
    }
    const pc = this.pc;
    if (!pc || pc.signalingState === 'closed') return this.restart(`no-pc:${reason}`);
    // Several triggers fire for one network change; with an offer outstanding, resending it
    // is enough (it may have been lost).
    if (pc.signalingState === 'have-local-offer') return this.#resendOffer(pc);
    this.timings.iceRestarts++;
    this.#escalateLater(pc);
    try {
      await pc.setLocalDescription(await pc.createOffer({ iceRestart: true }));
      this.#sendDesc(pc);
      this.#armOfferResend(pc);
    } catch (e) {
      this.log(`ice restart: ${(e as Error).message}`);
      this.restart('ice-restart-error');
    }
  }

  /** Sends again what the remote may have missed: the pending offer and every local candidate. */
  resync(reason: string): void {
    const pc = this.pc;
    if (!pc || pc.signalingState === 'closed') return;
    this.ev(`resync:${reason}`);
    if (pc.signalingState === 'have-local-offer') this.#resendOffer(pc);
    for (const cand of this.#localCandidates) this.room.send(this.remote, { kind: 'cand', gen: this.gen, cand } satisfies Signal);
  }

  close(): void {
    clearTimeout(this.#escalation);
    clearInterval(this.#offerTimer);
    clearInterval(this.#candTimer);
    this.pc?.close();
    this.pc = null;
  }

  sendCtl(msg: unknown): boolean {
    if (!this.ready) return false;
    this.ctl!.send(JSON.stringify(msg));
    return true;
  }

  /** Signals are handled one at a time: a resent offer must not interleave with the first. */
  onSignal(data: Signal): Promise<void> {
    this.#chain = this.#chain.then(() => this.#handle(data)).catch((e) => this.log(`signal: ${(e as Error).message}`));
    return this.#chain;
  }

  async #handle(data: Signal): Promise<void> {
    if (data.kind === 'restart') {
      if (this.initiator) this.restart('remote-request');
      return;
    }
    if (data.kind === 'ice-restart') {
      if (this.initiator) await this.iceRestart(`remote:${data.reason}`);
      return;
    }
    if (data.kind === 'desc') {
      if (data.sdp.type === 'offer') {
        // Same generation + live connection = renegotiation (ICE restart) on the same pc.
        if (data.gen !== this.gen || !this.pc || this.pc.signalingState === 'closed') this.#open(data.gen);
        const pc = this.pc!;
        await pc.setRemoteDescription(data.sdp);
        await this.#flushCandidates();
        await pc.setLocalDescription(await pc.createAnswer());
        this.#sendDesc(pc);
      } else if (data.gen === this.gen && this.pc?.signalingState === 'have-local-offer') {
        await this.pc.setRemoteDescription(data.sdp);
        await this.#flushCandidates();
      }
      return;
    }
    if (data.kind === 'cand' && data.gen === this.gen && this.pc) {
      if (this.pc.remoteDescription) await this.pc.addIceCandidate(data.cand).catch((e) => this.log(`ice: ${e.message}`));
      else this.#pendingCandidates.push(data.cand);
    }
  }

  #sendDesc(pc: RTCPeerConnection): void {
    this.room.send(this.remote, { kind: 'desc', gen: this.gen, sdp: pc.localDescription!.toJSON() } satisfies Signal);
  }

  async #flushCandidates(): Promise<void> {
    for (const c of this.#pendingCandidates.splice(0)) {
      await this.pc?.addIceCandidate(c).catch((e) => this.log(`ice: ${e.message}`));
    }
  }

  #resendOffer(pc: RTCPeerConnection): void {
    if (pc !== this.pc || pc.signalingState !== 'have-local-offer') return;
    this.ev('offer:resend');
    this.#sendDesc(pc);
  }

  #armOfferResend(pc: RTCPeerConnection): void {
    clearInterval(this.#offerTimer);
    let n = 0;
    this.#offerTimer = setInterval(() => {
      if (pc !== this.pc || pc.signalingState !== 'have-local-offer' || ++n > OFFER_RESENDS) return clearInterval(this.#offerTimer);
      this.#resendOffer(pc);
    }, OFFER_RESEND_MS);
  }

  #armCandidateResend(pc: RTCPeerConnection): void {
    clearInterval(this.#candTimer);
    let n = 0;
    this.#candTimer = setInterval(() => {
      if (pc !== this.pc || pc.connectionState === 'connected' || ++n > CANDIDATE_RESENDS) return clearInterval(this.#candTimer);
      this.resync('timer');
    }, CANDIDATE_RESEND_MS);
  }

  #escalateLater(pc: RTCPeerConnection): void {
    clearTimeout(this.#escalation);
    this.#escalation = setTimeout(() => {
      if (pc === this.pc && pc.connectionState !== 'connected') this.restart('escalation');
    }, ESCALATE_MS);
  }

  #open(gen: number): void {
    clearTimeout(this.#escalation);
    this.pc?.close();
    this.gen = gen;
    this.#pendingCandidates = [];
    this.#localCandidates = [];
    this.ev(`open:gen${gen}`);
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    this.pc = pc;
    let grace: ReturnType<typeof setTimeout> | undefined;

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const cand = e.candidate.toJSON();
      this.#localCandidates.push(cand);
      this.room.send(this.remote, { kind: 'cand', gen, cand } satisfies Signal);
    };
    // An ICE restart gathers a new set of candidates; the old ones are useless to the remote.
    pc.onicegatheringstatechange = () => {
      if (pc === this.pc && pc.iceGatheringState === 'gathering') this.#localCandidates = [];
    };
    pc.onconnectionstatechange = () => {
      if (pc !== this.pc) return;
      const state = pc.connectionState;
      this.ev(`pc:${state}`);
      clearTimeout(grace);
      if (state === 'connecting' || state === 'disconnected') this.#armCandidateResend(pc);
      if (state === 'connected') {
        clearTimeout(this.#escalation);
        clearInterval(this.#candTimer);
        this.timings.pcConnectedMs ??= performance.now() - this.#t0;
        void this.path().then((p) => {
          if (p) this.lastPath = p;
          this.dispatchEvent(new CustomEvent('state', { detail: { state } }));
        });
      }
      // 'disconnected' often heals by itself; give it a moment before renegotiating.
      if (state === 'disconnected') {
        grace = setTimeout(() => {
          if (pc === this.pc && pc.connectionState !== 'connected') void this.iceRestart('disconnected');
        }, DISCONNECTED_GRACE_MS);
      }
      if (state === 'failed') void this.iceRestart('failed');
      this.dispatchEvent(new CustomEvent('state', { detail: { state } }));
    };
    pc.ondatachannel = (e) => this.dispatchEvent(new CustomEvent('channel', { detail: { channel: e.channel } }));

    const ctl = pc.createDataChannel('ctl', { negotiated: true, id: 0 });
    this.ctl = ctl;
    ctl.onopen = () => {
      this.ev('ctl:open');
      this.dispatchEvent(new Event('ready'));
    };
    ctl.onclose = () => {
      if (ctl !== this.ctl) return;
      this.ev('ctl:close');
      this.dispatchEvent(new Event('down'));
    };
    ctl.onmessage = (e) => {
      let msg: unknown;
      try {
        msg = JSON.parse(String(e.data));
      } catch {
        return this.log('ctl: invalid frame');
      }
      this.dispatchEvent(new CustomEvent('ctl', { detail: { msg } }));
    };

    if (this.initiator) {
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .then(() => {
          this.#sendDesc(pc);
          this.#armOfferResend(pc);
        })
        .catch((e) => this.log(`offer: ${e.message}`));
    }
  }

  /** Selected ICE path, by candidate type only — addresses are deliberately not recorded. */
  async path(): Promise<PathInfo | null> {
    if (!this.pc) return null;
    const stats = await this.pc.getStats();
    let pair: Record<string, any> | null = null;
    stats.forEach((r) => {
      if (r.type === 'transport' && r.selectedCandidatePairId) pair = stats.get(r.selectedCandidatePairId);
    });
    if (!pair) {
      stats.forEach((r) => {
        if (r.type === 'candidate-pair' && r.state === 'succeeded' && (r.nominated || r.selected)) pair = r;
      });
    }
    if (!pair) return null;
    const p = pair as Record<string, any>;
    const local = stats.get(p.localCandidateId) ?? {};
    const remote = stats.get(p.remoteCandidateId) ?? {};
    return {
      local: local.candidateType ?? '?',
      remote: remote.candidateType ?? '?',
      protocol: local.protocol ?? '?',
      ipVersion: local.address ? (String(local.address).includes(':') ? 6 : 4) : null,
      rttMs: p.currentRoundTripTime != null ? Math.round(p.currentRoundTripTime * 1000) : null,
      maxMessageSize: this.pc.sctp?.maxMessageSize ?? null,
    };
  }
}
